import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import SearchableSelect from './common/SearchableSelect';
import AdminMenu from './common/menus/AdminMenu';
import ProfessorMenu from './common/menus/ProfessorMenu';
import AlumnoMenu from './common/menus/AlumnoMenu';
import ParticularAlumnoMenu from './common/menus/ParticularAlumnoMenu';
import ParticularDashboardMenu from './common/menus/ParticularDashboardMenu';
import SubscriptionBlockedModal from './common/SubscriptionBlockedModal';
import { AulaEmptyState, AulaSection } from './common/AulaShell';
import StudentShell, { StudentModal } from './common/StudentShell';
import { buildParticularPreceptorMenuItems } from './common/menus/ParticularPreceptorMenu';
import { buildParticularAlumnoMenuItems } from './common/menus/ParticularAlumnoMenu';
import { useAuth } from '../contexts/AuthContext';
import ColegioService from '../services/ColegioService';
import mensajeriaInternaService from '../services/MensajeriaInternaService';
import ParticularService from '../services/ParticularService';
import particularesApiService from '../services/ParticularesApiService';
import UsuarioService from '../services/UsuarioService';
import useMensajesNoLeidos, { MENSAJES_NO_LEIDOS_REFRESH_EVENT } from '../hooks/useMensajesNoLeidos';
import { hasOpenAIChatsEnabled, isFeatureEnabled } from '../utils/adminMenu';
import {
  CALENDARIO_SUBSCRIPTION_BLOCKED_MESSAGE,
  isParticularCalendarioAllowed,
  isParticularCalendarioMenuItem,
  isParticularChatGPTAllowed,
  isParticularChatGPTMenuItem
} from '../utils/particularChatGPTAccess';

const POLL_MS = 5 * 60 * 1000;

const getRoleName = (user) => (user?.roleName || user?.nombreRol || user?.role || '').toLowerCase();

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const getMensajeId = (item) => item?.id || item?.Id;
const getMensajeAsunto = (item) => item?.asunto || item?.Asunto || 'Sin asunto';
const getMensajeTexto = (item) => item?.mensaje || item?.Mensaje || item?.preview || item?.Preview || '';
const getNestedUsuario = (item, lowerKey, upperKey) => item?.[lowerKey] || item?.[upperKey] || null;
const getPersonaNombre = (item) => item?.displayName || item?.DisplayName || item?.nombre || item?.Nombre || item?.email || item?.Email || '';
const getPersonaEmail = (item) => item?.email || item?.Email || '';
const getMensajeEmisor = (item) => (
  item?.emisorNombre ||
  item?.EmisorNombre ||
  getPersonaNombre(getNestedUsuario(item, 'emisor', 'Emisor')) ||
  item?.emisorEmail ||
  item?.EmisorEmail ||
  'Usuario'
);
const getMensajeEmail = (item) => item?.emisorEmail || item?.EmisorEmail || getPersonaEmail(getNestedUsuario(item, 'emisor', 'Emisor')) || '';
const getMensajeFecha = (item) => item?.fechaCreacion || item?.FechaCreacion;
const isMensajeUnread = (item) => !(item?.leido ?? item?.Leido);
const getMensajeEstado = (item) => item?.estadoProcesamiento || item?.EstadoProcesamiento || '';
const getMensajeCantidadDestinatarios = (item) => Number(item?.cantidadDestinatarios ?? item?.CantidadDestinatarios ?? 0) || 0;
const getMensajeCantidadEncolados = (item) => Number(item?.cantidadDestinatariosEncolados ?? item?.CantidadDestinatariosEncolados ?? item?.cantidadDestinatarios ?? item?.CantidadDestinatarios ?? 0) || 0;
const getMensajeTotalLeidos = (item) => Number(item?.totalLeidos ?? item?.TotalLeidos ?? 0) || 0;
const getMensajeTotalNoLeidos = (item) => Number(item?.totalNoLeidos ?? item?.TotalNoLeidos ?? 0) || 0;
const getMensajeTotalPushErrores = (item) => Number(item?.totalPushErrores ?? item?.TotalPushErrores ?? 0) || 0;
const getUsuarioOptionId = (usuario) => usuario?.id || usuario?.Id || '';
const getUsuarioOptionRole = (usuario) => String(usuario?.rol || usuario?.Rol || '').toLowerCase();
const getDetalleMensaje = (detail) => detail?.mensaje || detail?.Mensaje || null;
const getDetalleDestinatarios = (detail) => {
  const direct = detail?.destinatarios || detail?.Destinatarios;
  if (Array.isArray(direct)) return direct;
  const mensaje = getDetalleMensaje(detail);
  const nested = mensaje?.destinatarios || mensaje?.Destinatarios;
  return Array.isArray(nested) ? nested : [];
};
const getDetalleTotalLeidos = (detail) => Number(detail?.totalLeidos ?? detail?.TotalLeidos ?? getMensajeTotalLeidos(getDetalleMensaje(detail))) || 0;
const getDetalleTotalNoLeidos = (detail) => Number(detail?.totalNoLeidos ?? detail?.TotalNoLeidos ?? getMensajeTotalNoLeidos(getDetalleMensaje(detail))) || 0;
const getDetallePorcentajeLeidos = (detail) => Number(detail?.porcentajeLeidos ?? detail?.PorcentajeLeidos ?? 0) || 0;
const getDestinatarioUsuario = (item) => getNestedUsuario(item, 'usuario', 'Usuario') || getNestedUsuario(item, 'usuarioDestinatario', 'UsuarioDestinatario');
const getDestinatarioNombre = (item) => item?.nombre || item?.Nombre || getPersonaNombre(getDestinatarioUsuario(item)) || item?.email || item?.Email || 'Usuario';
const getDestinatarioEmail = (item) => item?.email || item?.Email || getPersonaEmail(getDestinatarioUsuario(item)) || '';
const getDestinatarioRol = (item) => item?.rol || item?.Rol || 'Usuario';
const getDestinatarioPushEstado = (item) => item?.pushEstado || item?.PushEstado || 'Pendiente';
const getDestinatarioPushError = (item) => item?.pushError || item?.PushError || '';
const getDestinatarioPushDispositivos = (item) => Number(item?.totalPushDispositivos ?? item?.TotalPushDispositivos ?? 0) || 0;
const getDestinatarioPushEnviadas = (item) => Number(item?.totalPushEnviadas ?? item?.TotalPushEnviadas ?? 0) || 0;
const getDestinatarioPushErrores = (item) => Number(item?.totalPushErrores ?? item?.TotalPushErrores ?? 0) || 0;
const getDestinatarioFechaPush = (item) => item?.fechaPushUtc || item?.FechaPushUtc;
const isDestinatarioLeido = (item) => Boolean(item?.leido ?? item?.Leido);
const getDestinatarioFechaLectura = (item) => item?.fechaLectura || item?.FechaLectura;

const buildPreview = (mensaje) => {
  const normalized = String(mensaje || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return 'Sin contenido.';
  return normalized.length > 160 ? `${normalized.slice(0, 157)}...` : normalized;
};

const getPushStatusMeta = (estado) => {
  const normalized = String(estado || '').toLowerCase();
  if (normalized === 'enviada') return { label: 'Push enviada', tone: 'success' };
  if (normalized === 'parcial') return { label: 'Push parcial', tone: 'warning' };
  if (normalized === 'error') return { label: 'Error push', tone: 'danger' };
  if (normalized === 'sinsuscripcion') return { label: 'Sin dispositivo', tone: 'muted' };
  if (normalized === 'omitidaporpreferencias') return { label: 'Preferencia desactivada', tone: 'muted' };
  if (normalized === 'noconfigurado') return { label: 'Push no configurada', tone: 'warning' };
  return { label: estado || 'Pendiente', tone: 'muted' };
};

const DEFAULT_COMPOSER_FORM = {
  asunto: '',
  mensaje: '',
  destinatarioIds: [],
  cursoIds: [],
  familiarIds: [],
  alumnoFamiliarIds: [],
  incluirTodaLaEscuela: false,
  incluirTodosLosProfesores: false,
  incluirTodosLosAlumnos: false,
  incluirTodosLosFamiliares: false
};

const DEFAULT_COMPOSER_OPTIONS = {
  usuarios: [],
  cursos: [],
  alumnosFamiliares: [],
  familiares: [],
  totalProfesores: 0,
  totalAlumnos: 0,
  totalFamiliares: 0,
  // Solo particulares: grupos habilitados según el ámbito del usuario.
  puedeSegmentar: false,
  puedeEnviarATodaLaCuenta: false
};

// El dueño de una cuenta particular figura con rol "Dueño de cuenta", pero es un profesor más.
const isProfesorUsuarioOption = (usuario) => (
  getUsuarioOptionRole(usuario).includes('profesor') || Boolean(usuario?.esDueno ?? usuario?.EsDueno)
);

const MensajesInternosInbox = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, updateToken } = useAuth();
  const [mensajes, setMensajes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [studentDetailOpen, setStudentDetailOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updatingRead, setUpdatingRead] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterLeido, setFilterLeido] = useState('todos');
  const [activeMailbox, setActiveMailbox] = useState('recibidos');
  const [total, setTotal] = useState(0);
  const [totalNoLeidos, setTotalNoLeidos] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerAudience, setComposerAudience] = useState('');
  const [composerOptions, setComposerOptions] = useState(DEFAULT_COMPOSER_OPTIONS);
  const [composerOptionsLoaded, setComposerOptionsLoaded] = useState(false);
  const [composerLoading, setComposerLoading] = useState(false);
  const [composerSending, setComposerSending] = useState(false);
  const [composerError, setComposerError] = useState('');
  const [composerSuccess, setComposerSuccess] = useState('');
  const [composerForm, setComposerForm] = useState(DEFAULT_COMPOSER_FORM);
  const [sentDetailOpen, setSentDetailOpen] = useState(false);
  const [sentDetail, setSentDetail] = useState(null);
  const [sentDetailLoading, setSentDetailLoading] = useState(false);
  const [sentDetailError, setSentDetailError] = useState('');
  const [sentDetailSearch, setSentDetailSearch] = useState('');
  const [particularContextReady, setParticularContextReady] = useState(false);
  const [particularContextError, setParticularContextError] = useState('');
  const [particularContext, setParticularContext] = useState(null);
  const [subscriptionBlockedOpen, setSubscriptionBlockedOpen] = useState(false);
  const [subscriptionBlockedMessage, setSubscriptionBlockedMessage] = useState('');
  const searchDebounceRef = useRef(null);
  const syncedParticularRef = useRef('');

  const roleName = getRoleName(user);
  const isAdmin = roleName.includes('admin');
  const isAlumno = roleName.includes('alumno');
  const isProfesor = roleName.includes('profesor');
  const activeParticular = useMemo(() => ParticularService.getActiveParticular(), []);
  const activeParticularId = useMemo(() => (
    activeParticular?.id || activeParticular?.Id || ParticularService.getActiveParticularId()
  ), [activeParticular]);
  // El preceptor (por ahora solo en cuentas particulares) usa la bandeja con el armazón del alumno.
  const isPreceptor = roleName === 'preceptor';
  const isParticularRole = roleName.includes('particular') || isPreceptor;
  const requestedScope = location.state?.scope || new URLSearchParams(location.search).get('scope');
  const isParticularMode = requestedScope === 'particulares' || requestedScope === 'particular' || Boolean(activeParticular) || Boolean(activeParticularId) || isParticularRole;
  const canAccess = isAdmin || isProfesor || isAlumno || isParticularRole;
  const canCompose = isAdmin || isProfesor || isAlumno || isParticularRole;
  const usaArmazonAlumno = isParticularMode && (isAlumno || isPreceptor);
  const isProfesorComposer = isProfesor && !isAdmin;
  const isAlumnoComposer = isAlumno && !isAdmin && !isProfesor;
  const featureFlags = useMemo(() => ColegioService.getFeatureFlags(), []);
  const hasOpenAIChats = useMemo(() => hasOpenAIChatsEnabled(featureFlags), [featureFlags]);
  const hasAgentesClasicos = useMemo(() => isFeatureEnabled(featureFlags, 'PermiteAgentesClasicosGPT'), [featureFlags]);
  const hasModuloAlumnos = useMemo(() => isFeatureEnabled(featureFlags, 'ModuloAlumnos'), [featureFlags]);
  const activeColegio = useMemo(() => ColegioService.getActiveColegio() || ColegioService.getColegios()?.[0] || null, []);
  const activeColegioId = useMemo(() => ColegioService.getActiveColegioId() || activeColegio?.id || activeColegio?.Id || null, [activeColegio]);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';
  const activeParticularName = activeParticular?.nombre || activeParticular?.Nombre || activeParticular?.name || activeParticular?.Name || 'Particular';
  const scopeName = isParticularMode ? activeParticularName : activeColegioName;
  const isSentMailbox = activeMailbox === 'enviados';
  const effectiveParticular = particularContext || activeParticular;
  const canUseParticularChatGPTFeatures = !isParticularMode || isParticularChatGPTAllowed(effectiveParticular);
  const canUseParticularCalendarioFeature = !isParticularMode || isParticularCalendarioAllowed(effectiveParticular);
  const canSendFamiliares = !isParticularMode && (isAdmin || isProfesor);
  const isFamiliaresComposer = composerAudience === 'familiares';
  // En particulares el ámbito lo define el back: el dueño/administradores ven toda la cuenta; el profesor, sus cursos.
  const isParticularSegmentado = isParticularMode && composerOptions.puedeSegmentar;
  const isScopedProfesorComposer = isParticularMode ? !composerOptions.puedeEnviarATodaLaCuenta : isProfesorComposer;
  const puedeEnviarATodos = isParticularMode ? composerOptions.puedeEnviarATodaLaCuenta : !isProfesorComposer;
  const { count: mensajesNoLeidos } = useMensajesNoLeidos(
    canAccess && (!isParticularMode || particularContextReady),
    { scope: isParticularMode ? 'particular' : 'colegio' }
  );
  const displayedNoLeidos = isParticularMode ? mensajesNoLeidos : totalNoLeidos;

  const destinatarioOptions = useMemo(() => (
    (composerOptions.usuarios || []).map((usuario) => {
      const cursos = usuario.cursos || usuario.Cursos || [];
      const cursosText = Array.isArray(cursos)
        ? cursos.map((curso) => curso?.nombre || curso?.Nombre || '').filter(Boolean).join(', ')
        : '';
      const nombre = usuario.displayName || usuario.DisplayName || usuario.nombre || usuario.Nombre || usuario.email || usuario.Email || 'Usuario';
      const rol = usuario.rol || usuario.Rol || 'Usuario';
      const email = usuario.email || usuario.Email || '';
      return {
        value: getUsuarioOptionId(usuario),
        label: nombre,
        secondary: `${email}${email ? ' · ' : ''}${rol}${cursosText ? ` · ${cursosText}` : ''}`,
        searchText: `${nombre} ${email} ${rol} ${cursosText}`
      };
    })
  ), [composerOptions.usuarios]);

  const profesorIdsSet = useMemo(() => new Set(
    (composerOptions.usuarios || [])
      .filter(isProfesorUsuarioOption)
      .map((usuario) => String(getUsuarioOptionId(usuario)))
      .filter(Boolean)
  ), [composerOptions.usuarios]);

  const alumnoIdsSet = useMemo(() => new Set(
    (composerOptions.usuarios || [])
      .filter((usuario) => !isProfesorUsuarioOption(usuario) && getUsuarioOptionRole(usuario).includes('alumno'))
      .map((usuario) => String(getUsuarioOptionId(usuario)))
      .filter(Boolean)
  ), [composerOptions.usuarios]);

  const profesorOptions = useMemo(() => (
    destinatarioOptions.filter((option) => profesorIdsSet.has(String(option.value)))
  ), [destinatarioOptions, profesorIdsSet]);

  const preceptorIdsSet = useMemo(() => new Set(
    (composerOptions.usuarios || [])
      .filter((usuario) => getUsuarioOptionRole(usuario) === 'preceptor')
      .map((usuario) => String(getUsuarioOptionId(usuario)))
      .filter(Boolean)
  ), [composerOptions.usuarios]);

  const preceptorOptions = useMemo(() => (
    destinatarioOptions.filter((option) => preceptorIdsSet.has(String(option.value)))
  ), [destinatarioOptions, preceptorIdsSet]);

  const selectedPreceptorIds = useMemo(() => (
    (composerForm.destinatarioIds || []).filter((id) => preceptorIdsSet.has(String(id)))
  ), [composerForm.destinatarioIds, preceptorIdsSet]);

  const alumnoOptions = useMemo(() => (
    destinatarioOptions.filter((option) => alumnoIdsSet.has(String(option.value)))
  ), [alumnoIdsSet, destinatarioOptions]);

  const selectedProfesorIds = useMemo(() => (
    (composerForm.destinatarioIds || []).filter((id) => profesorIdsSet.has(String(id)))
  ), [composerForm.destinatarioIds, profesorIdsSet]);

  const selectedAlumnoIds = useMemo(() => (
    (composerForm.destinatarioIds || []).filter((id) => alumnoIdsSet.has(String(id)))
  ), [alumnoIdsSet, composerForm.destinatarioIds]);

  const cursoOptions = useMemo(() => (
    (composerOptions.cursos || []).map((curso) => {
      const cantidadAlumnos = Number(curso.cantidadAlumnos ?? curso.CantidadAlumnos ?? 0) || 0;
      const cantidadFamiliares = Number(curso.cantidadFamiliares ?? curso.CantidadFamiliares ?? 0) || 0;
      // En particulares con "modo colegio" el curso trae su año/división.
      const division = isParticularMode ? curso.division || curso.Division || '' : '';
      return {
        value: curso.id || curso.Id,
        label: curso.nombre || curso.Nombre || 'Curso',
        secondary: isFamiliaresComposer
          ? `${cantidadFamiliares} ${cantidadFamiliares === 1 ? 'familiar' : 'familiares'}`
          : `${division ? `${division} · ` : ''}${cantidadAlumnos} ${cantidadAlumnos === 1 ? 'alumno' : 'alumnos'}`,
        searchText: `${curso.nombre || curso.Nombre || ''} ${curso.valorAnio || curso.ValorAnio || ''} ${curso.division || curso.Division || ''} ${curso.cicloLectivo || curso.CicloLectivo || ''}`
      };
    })
  ), [composerOptions.cursos, isFamiliaresComposer, isParticularMode]);

  const alumnoFamiliaresOptions = useMemo(() => (
    (composerOptions.alumnosFamiliares || []).map((alumno) => {
      const cantidadFamiliares = Number(alumno.cantidadFamiliares ?? alumno.CantidadFamiliares ?? 0) || 0;
      return {
        value: alumno.id || alumno.Id,
        label: alumno.nombre || alumno.Nombre || alumno.email || alumno.Email || 'Alumno',
        secondary: `${alumno.cursos || alumno.Cursos || ''} · ${cantidadFamiliares} ${cantidadFamiliares === 1 ? 'familiar' : 'familiares'}`,
        searchText: `${alumno.nombre || alumno.Nombre || ''} ${alumno.email || alumno.Email || ''} ${alumno.cursos || alumno.Cursos || ''}`
      };
    })
  ), [composerOptions.alumnosFamiliares]);

  const familiarOptions = useMemo(() => (
    (composerOptions.familiares || []).map((familiar) => ({
      value: familiar.id || familiar.Id,
      label: familiar.nombre || familiar.Nombre || familiar.email || familiar.Email || 'Familiar',
      secondary: `${familiar.email || familiar.Email || ''} · ${familiar.alumnos || familiar.Alumnos || ''}`,
      searchText: `${familiar.nombre || familiar.Nombre || ''} ${familiar.email || familiar.Email || ''} ${familiar.alumnos || familiar.Alumnos || ''} ${familiar.cursos || familiar.Cursos || ''}`
    }))
  ), [composerOptions.familiares]);

  const selectedRecipientCount = useMemo(() => {
    if (isFamiliaresComposer) {
      const ids = new Set((composerForm.familiarIds || []).filter(Boolean));

      if (composerForm.incluirTodosLosFamiliares) {
        (composerOptions.familiares || []).forEach((familiar) => ids.add(familiar.id || familiar.Id));
      }

      const selectedAlumnoIds = new Set((composerForm.alumnoFamiliarIds || []).filter(Boolean));
      (composerOptions.alumnosFamiliares || [])
        .filter((alumno) => selectedAlumnoIds.has(alumno.id || alumno.Id))
        .forEach((alumno) => {
          const familiarIds = alumno.familiarIds || alumno.FamiliarIds || [];
          familiarIds.forEach((id) => ids.add(id));
        });

      const selectedCursoIds = new Set((composerForm.cursoIds || []).filter(Boolean));
      (composerOptions.cursos || [])
        .filter((curso) => selectedCursoIds.has(curso.id || curso.Id))
        .forEach((curso) => {
          const familiarIds = curso.familiarIds || curso.FamiliarIds || [];
          familiarIds.forEach((id) => ids.add(id));
        });

      ids.delete(undefined);
      ids.delete(null);
      ids.delete('');
      return ids.size;
    }

    const ids = new Set((composerForm.destinatarioIds || []).filter(Boolean));
    const usuarios = composerOptions.usuarios || [];

    if (composerForm.incluirTodaLaEscuela) {
      usuarios.forEach((usuario) => ids.add(usuario.id || usuario.Id));
    } else {
      if (composerForm.incluirTodosLosProfesores) {
        profesorIdsSet.forEach((id) => ids.add(id));
      }

      if (composerForm.incluirTodosLosAlumnos) {
        alumnoIdsSet.forEach((id) => ids.add(id));
      }
    }

    const selectedCursoIds = new Set((composerForm.cursoIds || []).filter(Boolean));
    (composerOptions.cursos || [])
      .filter((curso) => selectedCursoIds.has(curso.id || curso.Id))
      .forEach((curso) => {
        const alumnoIds = curso.alumnoIds || curso.AlumnoIds || [];
        alumnoIds.forEach((id) => ids.add(id));
      });

    ids.delete(undefined);
    ids.delete(null);
    ids.delete('');
    return new Set([...ids].map(String)).size;
  }, [alumnoIdsSet, composerForm, composerOptions, isFamiliaresComposer, profesorIdsSet]);

  const filteredSentDetailRecipients = useMemo(() => {
    const recipients = getDetalleDestinatarios(sentDetail);
    const term = sentDetailSearch.trim().toLowerCase();
    if (!term) return recipients;

    return recipients.filter((recipient) => (
      `${getDestinatarioNombre(recipient)} ${getDestinatarioEmail(recipient)} ${getDestinatarioRol(recipient)} ${getDestinatarioPushEstado(recipient)}`
        .toLowerCase()
        .includes(term)
    ));
  }, [sentDetail, sentDetailSearch]);

  useEffect(() => {
    if (!isParticularMode) {
      setParticularContextReady(true);
      setParticularContextError('');
      setParticularContext(null);
      return;
    }

    if (!activeParticularId) {
      setParticularContextReady(false);
      setParticularContextError('Debe seleccionar un particular para consultar mensajes.');
      setParticularContext(null);
      return;
    }

    if (syncedParticularRef.current === String(activeParticularId)) {
      setParticularContextReady(true);
      setParticularContextError('');
      return;
    }

    let cancelled = false;
    setParticularContextReady(false);
    setParticularContextError('');

    UsuarioService.selectParticular(activeParticularId)
      .then((response) => {
        if (cancelled) return;
        const token = response?.token || response?.Token;
        if (token) {
          updateToken(token);
        }
        syncedParticularRef.current = String(activeParticularId);
        return particularesApiService.getContexto();
      })
      .then((contextResponse) => {
        if (cancelled) return;
        setParticularContext(contextResponse?.particular || contextResponse?.Particular || activeParticular || null);
        setParticularContextReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setParticularContextError(err?.message || 'No pudimos seleccionar el particular activo.');
        setParticularContextReady(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeParticular, activeParticularId, isParticularMode, updateToken]);

  const loadMensajes = useCallback(async () => {
    if (isParticularMode && !particularContextReady) {
      setMensajes([]);
      setSelected(null);
      setTotal(0);
      setTotalNoLeidos(0);
      if (particularContextError) {
        setError(particularContextError);
      }
      return;
    }

    if (!isParticularMode && !activeColegioId) {
      setError('Debe seleccionar un colegio para consultar mensajes.');
      setMensajes([]);
      setSelected(null);
      setTotal(0);
      setTotalNoLeidos(0);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = isSentMailbox
        ? await (isParticularMode ? particularesApiService : mensajeriaInternaService).getMensajesEnviados({
          skip: 0,
          take: 50,
          search
        })
        : await (isParticularMode ? particularesApiService : mensajeriaInternaService).getMensajes({
          skip: 0,
          take: 50,
          search,
          leido: filterLeido
        });
      const items = response?.mensajes || response?.Mensajes || [];
      const normalizedItems = Array.isArray(items) ? items : [];
      setMensajes(normalizedItems);
      setTotal(Number(response?.total ?? response?.Total ?? normalizedItems.length) || 0);
      if (!isSentMailbox) {
        setTotalNoLeidos(isParticularMode
          ? normalizedItems.filter((item) => isMensajeUnread(item)).length
          : Number(response?.totalNoLeidos ?? response?.TotalNoLeidos ?? 0) || 0);
      }
      setSelected((prev) => {
        if (normalizedItems.length === 0) return null;
        const prevId = getMensajeId(prev);
        return normalizedItems.find((item) => getMensajeId(item) === prevId) || normalizedItems[0];
      });
    } catch (err) {
      setError(err?.message || 'No pudimos cargar tus mensajes.');
      setMensajes([]);
      setSelected(null);
      setTotal(0);
      setTotalNoLeidos(0);
    } finally {
      setLoading(false);
    }
  }, [activeColegioId, filterLeido, isParticularMode, isSentMailbox, particularContextError, particularContextReady, search]);

  useEffect(() => {
    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = window.setTimeout(loadMensajes, 400);
    return () => {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
      }
    };
  }, [loadMensajes]);

  useEffect(() => {
    const intervalId = window.setInterval(loadMensajes, POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [loadMensajes]);

  useEffect(() => {
    setComposerOptionsLoaded(false);
    setComposerOptions(DEFAULT_COMPOSER_OPTIONS);
  }, [activeColegioId, activeParticular?.id, activeParticular?.Id, isParticularMode]);

  const dispatchBadgeRefresh = () => {
    window.dispatchEvent(new Event(MENSAJES_NO_LEIDOS_REFRESH_EVENT));
  };

  const loadComposerOptions = useCallback(async (audience = composerAudience || 'institucional') => {
    if (!canCompose) return;

    setComposerLoading(true);
    setComposerError('');
    try {
      const response = isParticularMode
        ? await particularesApiService.getMensajeDestinatarios()
        : audience === 'familiares'
          ? (isAdmin
            ? await mensajeriaInternaService.getAdminFamiliaresComposerOptions()
            : await mensajeriaInternaService.getProfesorFamiliaresComposerOptions())
          : (isAdmin
            ? await mensajeriaInternaService.getAdminComposerOptions()
            : isProfesor
              ? await mensajeriaInternaService.getProfesorComposerOptions()
              : await mensajeriaInternaService.getAlumnoComposerOptions());
      const usuarios = isParticularMode
        ? response?.destinatarios || response?.Destinatarios || []
        : response?.usuarios || response?.Usuarios || [];
      setComposerOptions({
        usuarios,
        cursos: response?.cursos || response?.Cursos || [],
        alumnosFamiliares: response?.alumnosFamiliares || response?.AlumnosFamiliares || [],
        familiares: response?.familiares || response?.Familiares || [],
        totalProfesores: Number(response?.totalProfesores ?? response?.TotalProfesores ?? 0) || 0,
        totalAlumnos: Number(response?.totalAlumnos ?? response?.TotalAlumnos ?? 0) || 0,
        totalFamiliares: Number(response?.totalFamiliares ?? response?.TotalFamiliares ?? 0) || 0,
        puedeSegmentar: Boolean(response?.puedeSegmentar ?? response?.PuedeSegmentar),
        puedeEnviarATodaLaCuenta: Boolean(response?.puedeEnviarATodaLaCuenta ?? response?.PuedeEnviarATodaLaCuenta)
      });
      setComposerOptionsLoaded(true);
    } catch (err) {
      setComposerError(err?.message || 'No pudimos cargar los destinatarios.');
      setComposerOptions(DEFAULT_COMPOSER_OPTIONS);
      setComposerOptionsLoaded(false);
    } finally {
      setComposerLoading(false);
    }
  }, [canCompose, composerAudience, isAdmin, isParticularMode, isProfesor]);

  const handleMenuNavigate = (item) => {
    setIsSidebarOpen(false);
    if (!item) return;

    if (isParticularMode && isParticularChatGPTMenuItem(item) && !canUseParticularChatGPTFeatures) {
      setSubscriptionBlockedMessage('');
      setSubscriptionBlockedOpen(true);
      return;
    }

    if (isParticularMode && isParticularCalendarioMenuItem(item) && !canUseParticularCalendarioFeature) {
      setSubscriptionBlockedMessage(CALENDARIO_SUBSCRIPTION_BLOCKED_MESSAGE);
      setSubscriptionBlockedOpen(true);
      return;
    }

    if (item.path) {
      navigate(item.path, item.state ? { state: item.state } : undefined);
      return;
    }

    if (isAdmin) {
      if (item.id === 'agentes-clasicos-gpt') {
        navigate('/mis-agentes-clasicos-gpt');
        return;
      }
      if (item.id === 'aulas-docentes') {
        navigate('/aulas-docentes');
        return;
      }
      navigate('/admin-dashboard', { state: { tab: item.id } });
    }
  };

  const handleBackToSelectColegio = useCallback(() => {
    if (isParticularMode) {
      ParticularService.setActiveParticular(null);
      navigate('/admin/select-colegio', { replace: true, state: { scope: 'particulares' } });
      return;
    }

    ColegioService.clearColegioData();
    navigate(isAdmin ? '/admin/select-colegio' : '/select-colegio', { replace: true });
  }, [isAdmin, isParticularMode, navigate]);

  const handleMarkSelectedAsRead = async () => {
    if (!selected) return;
    if (isSentMailbox || !isMensajeUnread(selected) || updatingRead) return;
    const id = getMensajeId(selected);
    if (!id) return;

    setUpdatingRead(true);
    setError('');
    try {
      if (isParticularMode) {
        await particularesApiService.updateMensajeLeido(id, true);
      } else {
        await mensajeriaInternaService.updateLeido(id, true);
      }
      const fechaLectura = new Date().toISOString();
      setSelected((prev) => prev ? { ...prev, leido: true, Leido: true, fechaLectura, FechaLectura: fechaLectura } : prev);
      setMensajes((prev) => prev.map((item) => {
        if (getMensajeId(item) !== id) return item;
        return { ...item, leido: true, Leido: true, fechaLectura, FechaLectura: fechaLectura };
      }));
      setTotalNoLeidos((prev) => Math.max(0, prev - 1));
      dispatchBadgeRefresh();
      await loadMensajes();
    } catch (err) {
      setError(err?.message || 'No pudimos marcar el mensaje como leído.');
    } finally {
      setUpdatingRead(false);
    }
  };

  const openReceivedDetail = async (item) => {
    setSelected(item);
    if (!isParticularMode) return;

    const id = getMensajeId(item);
    if (!id) return;

    setError('');
    try {
      const response = await particularesApiService.getMensaje(id);
      const detail = getDetalleMensaje(response);
      if (detail) {
        setSelected(detail);
      }
    } catch (err) {
      setError(err?.message || 'No pudimos cargar el detalle del mensaje.');
    }
  };

  const openSentDetail = async (item) => {
    const id = getMensajeId(item);
    if (!id) return;

    setSelected(item);
    setSentDetailOpen(true);
    setSentDetail(null);
    setSentDetailSearch('');
    setSentDetailError('');
    setSentDetailLoading(true);

    try {
      const response = isParticularMode
        ? await particularesApiService.getMensajeEnviado(id)
        : await mensajeriaInternaService.getMensajeEnviadoDetalle(id);
      setSentDetail(response);
    } catch (err) {
      setSentDetailError(err?.message || 'No pudimos cargar el detalle del mensaje enviado.');
    } finally {
      setSentDetailLoading(false);
    }
  };

  const closeSentDetail = () => {
    setSentDetailOpen(false);
    setSentDetail(null);
    setSentDetailError('');
    setSentDetailSearch('');
  };

  const openComposer = async () => {
    setComposerOpen(true);
    setComposerSuccess('');
    setComposerError('');
    setComposerForm(DEFAULT_COMPOSER_FORM);

    if (isParticularMode) {
      setComposerAudience('institucional');
      await loadComposerOptions('institucional');
      return;
    }

    if (canSendFamiliares) {
      setComposerAudience('');
      setComposerOptionsLoaded(false);
      setComposerOptions(DEFAULT_COMPOSER_OPTIONS);
      return;
    }

    setComposerAudience('institucional');
    if (!composerOptionsLoaded) {
      await loadComposerOptions('institucional');
    }
  };

  const closeComposer = () => {
    if (composerSending) return;
    setComposerOpen(false);
    setComposerError('');
    setComposerAudience('');
    setComposerOptionsLoaded(false);
    setComposerOptions(DEFAULT_COMPOSER_OPTIONS);
    setComposerForm(DEFAULT_COMPOSER_FORM);
  };

  const selectComposerAudience = async (audience) => {
    setComposerAudience(audience);
    setComposerForm(DEFAULT_COMPOSER_FORM);
    setComposerOptions(DEFAULT_COMPOSER_OPTIONS);
    setComposerOptionsLoaded(false);
    setComposerError('');
    await loadComposerOptions(audience);
  };

  const updateComposerField = (field, value) => {
    setComposerForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateDestinatariosForSet = (targetSet, values) => {
    const nextValues = Array.isArray(values) ? values : [];
    setComposerForm((prev) => ({
      ...prev,
      destinatarioIds: [
        ...(prev.destinatarioIds || []).filter((id) => !targetSet.has(String(id))),
        ...nextValues
      ]
    }));
  };

  const handleMailboxChange = (mailbox) => {
    if (mailbox === activeMailbox) return;
    setActiveMailbox(mailbox);
    setMensajes([]);
    setSelected(null);
    setTotal(0);
    setError('');
  };

  const handleSendComposer = async (event) => {
    event.preventDefault();
    setComposerError('');
    setComposerSuccess('');

    const asunto = composerForm.asunto.trim();
    const mensaje = composerForm.mensaje.trim();
    if (!asunto) {
      setComposerError('El asunto es obligatorio.');
      return;
    }
    if (!mensaje) {
      setComposerError('El mensaje es obligatorio.');
      return;
    }
    if (selectedRecipientCount === 0) {
      setComposerError('Debe seleccionar al menos un destinatario.');
      return;
    }

    setComposerSending(true);
    try {
      let response;
      if (isParticularMode) {
        response = await particularesApiService.createMensaje({
          asunto,
          mensaje,
          destinatarioIds: composerForm.destinatarioIds,
          ...(composerOptions.puedeSegmentar ? {
            cursoIds: composerForm.cursoIds,
            incluirTodaLaCuenta: composerOptions.puedeEnviarATodaLaCuenta && composerForm.incluirTodaLaEscuela,
            incluirTodosLosProfesores: composerForm.incluirTodosLosProfesores,
            incluirTodosLosAlumnos: composerForm.incluirTodosLosAlumnos
          } : {})
        });
      } else if (isFamiliaresComposer) {
        const payload = {
          asunto,
          mensaje,
          familiarIds: composerForm.familiarIds,
          alumnoIds: composerForm.alumnoFamiliarIds,
          cursoIds: composerForm.cursoIds,
          incluirTodos: composerForm.incluirTodosLosFamiliares
        };
        response = isAdmin
          ? await mensajeriaInternaService.enviarMensajeFamiliaresAdmin(payload)
          : await mensajeriaInternaService.enviarMensajeFamiliaresProfesor(payload);
      } else {
        const payload = {
          asunto,
          mensaje,
          destinatarioIds: composerForm.destinatarioIds,
          cursoIds: composerForm.cursoIds,
          incluirTodosLosProfesores: composerForm.incluirTodosLosProfesores
        };
        response = isAdmin
          ? await mensajeriaInternaService.enviarMensajeAdmin({
            ...payload,
            incluirTodaLaEscuela: composerForm.incluirTodaLaEscuela,
            incluirTodosLosAlumnos: composerForm.incluirTodosLosAlumnos
          })
          : isProfesor
            ? await mensajeriaInternaService.enviarMensajeProfesor({
              ...payload,
              incluirTodosMisAlumnos: composerForm.incluirTodosLosAlumnos
            })
            : await mensajeriaInternaService.enviarMensajeAlumno({
              asunto,
              mensaje,
              destinatarioIds: composerForm.destinatarioIds
            });
      }

      const totalEncolados = response?.totalEncolados ?? response?.TotalEncolados ?? response?.totalDestinatarios ?? response?.TotalDestinatarios ?? selectedRecipientCount;
      setComposerSuccess(`Mensaje enviado a ${totalEncolados} ${Number(totalEncolados) === 1 ? 'destinatario' : 'destinatarios'}.`);
      setComposerOpen(false);
      setComposerAudience('');
      setComposerOptionsLoaded(false);
      setComposerOptions(DEFAULT_COMPOSER_OPTIONS);
      setComposerForm(DEFAULT_COMPOSER_FORM);
      if (activeMailbox === 'enviados') {
        await loadMensajes();
      } else {
        handleMailboxChange('enviados');
      }
    } catch (err) {
      setComposerError(err?.message || 'No pudimos enviar el mensaje.');
    } finally {
      setComposerSending(false);
    }
  };

  const renderMenu = () => {
    if (isParticularMode && isAlumno) {
      return (
        <ParticularAlumnoMenu
          activeId="mensajes"
          onNavigate={handleMenuNavigate}
          messageScope="particular"
        />
      );
    }

    if (isParticularMode) {
      return (
        <ParticularDashboardMenu
          activeId="mensajes"
          onNavigate={handleMenuNavigate}
          externalNavigation
          messageScope="particular"
        />
      );
    }

    if (isAdmin) {
      return (
        <AdminMenu
          activeId="mensajes"
          user={user}
          featureFlags={featureFlags}
          messageScope="colegio"
          onNavigate={handleMenuNavigate}
        />
      );
    }

    if (isAlumno) {
      return <AlumnoMenu activeId="mensajes" onNavigate={handleMenuNavigate} messageScope="colegio" />;
    }

    return (
      <ProfessorMenu
        activeId="mensajes"
        hasOpenAIChats={hasOpenAIChats}
        hasAgentesClasicos={hasAgentesClasicos}
        hasModuloAlumnos={hasModuloAlumnos}
        messageScope="colegio"
        onNavigate={handleMenuNavigate}
      />
    );
  };

  if (!canAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  // El alumno particular (y el preceptor) ven la bandeja con el mismo armazón que su panel y el aula.
  if (usaArmazonAlumno) {
    const sentDetailMessage = sentDetailOpen ? (getDetalleMensaje(sentDetail) || selected) : null;
    const sentLeidos = getDetalleTotalLeidos(sentDetail);
    const sentNoLeidos = getDetalleTotalNoLeidos(sentDetail);
    const sentTotal = sentLeidos + sentNoLeidos;
    const sentPorcentaje = getDetallePorcentajeLeidos(sentDetail) || (sentTotal > 0 ? Math.round((sentLeidos * 100) / sentTotal) : 0);
    const selectedUnread = !isSentMailbox && isMensajeUnread(selected);

    return (
      <StudentShell
        activeId="mensajes"
        onNavigate={handleMenuNavigate}
        user={user}
        onLogout={logout}
        particularName={scopeName}
        buildMenuItems={isPreceptor ? buildParticularPreceptorMenuItems : buildParticularAlumnoMenuItems}
        userFallbackLabel={isPreceptor ? 'Preceptor' : 'Alumno'}
      >
        <AulaSection
          active
          icon="fa-envelope"
          title="Mensajes"
          subtitle={isSentMailbox ? `${total} mensajes enviados` : `${total} mensajes · ${displayedNoLeidos} sin leer`}
          actions={(
            <>
              <button type="button" className="btn-secondary" onClick={loadMensajes} disabled={loading}>
                <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true" />
                Actualizar
              </button>
              {canCompose && (
                <button type="button" className="btn-primary" onClick={openComposer}>
                  <i className="fas fa-pen-to-square" aria-hidden="true" />
                  Nuevo mensaje
                </button>
              )}
            </>
          )}
        >
          {composerSuccess && (
            <div className="sp-notice">
              <i className="fas fa-check-circle" aria-hidden="true" />
              {composerSuccess}
            </div>
          )}

          <div className="sp-inbox-toolbar">
            <div className="sp-tabs">
              <button
                type="button"
                className={activeMailbox === 'recibidos' ? 'active' : ''}
                onClick={() => {
                  setStudentDetailOpen(false);
                  handleMailboxChange('recibidos');
                }}
              >
                Recibidos
              </button>
              <button
                type="button"
                className={activeMailbox === 'enviados' ? 'active' : ''}
                onClick={() => {
                  setStudentDetailOpen(false);
                  handleMailboxChange('enviados');
                }}
              >
                Enviados
              </button>
            </div>
            <div className="sp-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder={isSentMailbox ? 'Buscar por asunto o contenido' : 'Buscar por asunto, remitente o contenido'}
                aria-label="Buscar mensajes"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            {!isSentMailbox && (
              <select
                className="sp-select"
                aria-label="Filtrar por lectura"
                value={filterLeido}
                onChange={(event) => setFilterLeido(event.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="false">No leídos</option>
                <option value="true">Leídos</option>
              </select>
            )}
          </div>

          {error && <div className="aula-error small">{error}</div>}

          <div className={`sp-inbox ${studentDetailOpen && selected ? 'detail-open' : ''}`}>
            <div className="sp-inbox-list">
              {loading && <p className="sp-inbox-status">Cargando mensajes...</p>}
              {!loading && !error && mensajes.length === 0 && (
                <p className="sp-inbox-status">{isSentMailbox ? 'No hay mensajes enviados.' : 'No hay mensajes.'}</p>
              )}
              {mensajes.map((item) => {
                const id = getMensajeId(item);
                const unread = !isSentMailbox && isMensajeUnread(item);
                const quien = isSentMailbox
                  ? `${getMensajeCantidadDestinatarios(item)} destinatarios`
                  : getMensajeEmisor(item);
                return (
                  <button
                    key={id}
                    type="button"
                    className={`sp-mail-item ${unread ? 'unread' : ''} ${getMensajeId(selected) === id ? 'active' : ''}`}
                    onClick={() => {
                      if (isSentMailbox) {
                        openSentDetail(item);
                        return;
                      }
                      setStudentDetailOpen(true);
                      openReceivedDetail(item);
                    }}
                  >
                    <span className="sp-avatar" aria-hidden="true">
                      {isSentMailbox ? <i className="fas fa-paper-plane" /> : String(quien || '?').trim().charAt(0)}
                    </span>
                    <span className="sp-mail-copy">
                      <span className="sp-mail-top">
                        <span>{quien}</span>
                        <time>{formatDateTime(getMensajeFecha(item))}</time>
                      </span>
                      <span className="sp-mail-subject">{getMensajeAsunto(item)}</span>
                      <span className="sp-mail-preview">
                        {isSentMailbox
                          ? `${getMensajeTotalLeidos(item)} leídos · ${getMensajeTotalNoLeidos(item)} no leídos`
                          : buildPreview(getMensajeTexto(item))}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="sp-inbox-detail">
              {!selected ? (
                <AulaEmptyState icon="fa-envelope-open-text">
                  {isSentMailbox ? 'Elegí un mensaje enviado para ver quién lo leyó.' : 'Elegí un mensaje para leerlo.'}
                </AulaEmptyState>
              ) : (
                <article className="sp-mail" onClick={handleMarkSelectedAsRead} aria-busy={updatingRead}>
                  <button type="button" className="btn-secondary sp-mail-back" onClick={() => setStudentDetailOpen(false)}>
                    <i className="fas fa-arrow-left" aria-hidden="true" />
                    Volver a la bandeja
                  </button>
                  <div className="sp-mail-head">
                    <h3>{getMensajeAsunto(selected)}</h3>
                    <span className={`sp-chip ${selectedUnread ? 'brand' : ''}`}>
                      {isSentMailbox ? (getMensajeEstado(selected) || 'Enviado') : (selectedUnread ? 'No leído' : 'Leído')}
                    </span>
                  </div>
                  <div className="sp-mail-from">
                    <span className="sp-avatar" aria-hidden="true">
                      {isSentMailbox ? <i className="fas fa-paper-plane" /> : String(getMensajeEmisor(selected) || '?').trim().charAt(0)}
                    </span>
                    <div>
                      <strong>{isSentMailbox ? 'Mensaje enviado' : getMensajeEmisor(selected)}</strong>
                      <small>
                        {!isSentMailbox && getMensajeEmail(selected) ? `${getMensajeEmail(selected)} · ` : ''}
                        {formatDateTime(getMensajeFecha(selected))}
                      </small>
                    </div>
                  </div>
                  <div className="sp-mail-body">{getMensajeTexto(selected) || 'Sin contenido.'}</div>
                  {isSentMailbox && (
                    <>
                      <div className="sp-stats">
                        <span className="sp-chip">{getMensajeCantidadDestinatarios(selected)} destinatarios</span>
                        <span className="sp-chip ok">{getMensajeTotalLeidos(selected)} leídos</span>
                        <span className="sp-chip">{getMensajeTotalNoLeidos(selected)} no leídos</span>
                      </div>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={(event) => {
                          event.stopPropagation();
                          openSentDetail(selected);
                        }}
                      >
                        <i className="fas fa-chart-pie" aria-hidden="true" />
                        Ver quién lo leyó
                      </button>
                    </>
                  )}
                </article>
              )}
            </div>
          </div>
        </AulaSection>

        {composerOpen && (
          <StudentModal
            wide
            eyebrow="Mensajes"
            title="Nuevo mensaje"
            subtitle={composerOptions.puedeSegmentar
              ? 'Escribile a profesores, a todos tus alumnos, a cursos completos o a alumnos puntuales.'
              : 'Elegí uno o varios destinatarios habilitados para tu cuenta.'}
            closeOnOverlay={false}
            onClose={closeComposer}
            onSubmit={handleSendComposer}
            footer={(
              <>
                <span className="sp-chip">
                  <i className="fas fa-users" aria-hidden="true" />
                  {selectedRecipientCount} {selectedRecipientCount === 1 ? 'destinatario' : 'destinatarios'}
                </span>
                <button type="button" className="btn-secondary" onClick={closeComposer} disabled={composerSending}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={composerSending || composerLoading}>
                  <i className={`fas ${composerSending ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} aria-hidden="true" />
                  Enviar mensaje
                </button>
              </>
            )}
          >
            {composerError && <div className="aula-error small">{composerError}</div>}
            {composerOptions.puedeSegmentar ? (
              // Preceptor: profesores de la cuenta y alumnos de sus cursos, por grupo, por curso o a mano.
              <>
                <div className="sp-field">
                  Destinatarios rápidos
                  <small>{composerOptions.totalProfesores} profesores · {composerOptions.totalAlumnos} alumnos de tus cursos</small>
                  <div className="sp-quick-checks">
                    <label className={`sp-quick-check${composerForm.incluirTodosLosProfesores ? ' active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={composerForm.incluirTodosLosProfesores}
                        onChange={(event) => updateComposerField('incluirTodosLosProfesores', event.target.checked)}
                        disabled={composerSending || composerLoading || composerOptions.totalProfesores === 0}
                      />
                      <i className="fas fa-chalkboard-teacher" aria-hidden="true" />
                      <span>Todos los profesores</span>
                    </label>
                    <label className={`sp-quick-check${composerForm.incluirTodosLosAlumnos ? ' active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={composerForm.incluirTodosLosAlumnos}
                        onChange={(event) => updateComposerField('incluirTodosLosAlumnos', event.target.checked)}
                        disabled={composerSending || composerLoading || composerOptions.totalAlumnos === 0}
                      />
                      <i className="fas fa-user-graduate" aria-hidden="true" />
                      <span>Todos mis alumnos</span>
                    </label>
                  </div>
                </div>
                <div className="sp-field">
                  Profesores
                  <small>Profesores de la cuenta, incluido el dueño</small>
                  <SearchableSelect
                    multiple
                    values={selectedProfesorIds}
                    onChange={(values) => updateDestinatariosForSet(profesorIdsSet, values)}
                    options={profesorOptions}
                    placeholder={composerLoading ? 'Cargando profesores...' : 'Buscar profesor por nombre o mail'}
                    emptyText="No hay profesores disponibles."
                    noResultsText="No encontramos profesores."
                    disabled={composerSending || composerLoading || composerForm.incluirTodosLosProfesores}
                  />
                </div>
                <div className="sp-field">
                  Alumnos por curso
                  <small>Se incluyen todos los alumnos de cada curso elegido</small>
                  <SearchableSelect
                    multiple
                    values={composerForm.cursoIds}
                    onChange={(values) => updateComposerField('cursoIds', values)}
                    options={cursoOptions}
                    placeholder={composerLoading ? 'Cargando cursos...' : 'Buscar curso o división'}
                    emptyText="No tenés cursos asignados."
                    noResultsText="No encontramos cursos."
                    disabled={composerSending || composerLoading || composerForm.incluirTodosLosAlumnos}
                  />
                </div>
                <div className="sp-field">
                  Alumnos seleccionados a mano
                  <small>Solo alumnos de tus cursos</small>
                  <SearchableSelect
                    multiple
                    values={selectedAlumnoIds}
                    onChange={(values) => updateDestinatariosForSet(alumnoIdsSet, values)}
                    options={alumnoOptions}
                    placeholder={composerLoading ? 'Cargando alumnos...' : 'Buscar alumno por nombre, mail o curso'}
                    emptyText="No hay alumnos en tus cursos."
                    noResultsText="No encontramos alumnos."
                    disabled={composerSending || composerLoading || composerForm.incluirTodosLosAlumnos}
                  />
                </div>
              </>
            ) : (
              <div className="sp-field">
                Destinatarios
                <SearchableSelect
                  multiple
                  values={composerForm.destinatarioIds}
                  onChange={(values) => updateComposerField('destinatarioIds', values)}
                  options={destinatarioOptions}
                  placeholder={composerLoading ? 'Cargando destinatarios...' : 'Buscar por nombre, mail, rol o curso'}
                  emptyText="No hay destinatarios disponibles."
                  noResultsText="No encontramos destinatarios."
                  disabled={composerSending || composerLoading}
                />
              </div>
            )}
            <label className="sp-field">
              Asunto
              <input
                type="text"
                value={composerForm.asunto}
                onChange={(event) => updateComposerField('asunto', event.target.value)}
                maxLength={200}
                placeholder="Asunto del mensaje"
                disabled={composerSending}
              />
            </label>
            <label className="sp-field">
              Mensaje
              <textarea
                value={composerForm.mensaje}
                onChange={(event) => updateComposerField('mensaje', event.target.value)}
                rows={6}
                placeholder="Escribí tu mensaje"
                disabled={composerSending}
              />
            </label>
          </StudentModal>
        )}

        {sentDetailOpen && (
          <StudentModal
            wide
            eyebrow="Mensaje enviado"
            title={getMensajeAsunto(sentDetailMessage)}
            subtitle={sentDetailMessage ? formatDateTime(getMensajeFecha(sentDetailMessage)) : ''}
            onClose={closeSentDetail}
          >
            {sentDetailLoading && <p className="sp-inbox-status">Cargando detalle...</p>}
            {sentDetailError && <div className="aula-error small">{sentDetailError}</div>}

            {!sentDetailLoading && !sentDetailError && sentDetailMessage && (
              <>
                <div className="sp-read-summary">
                  <div
                    className="sp-donut"
                    style={{ background: `conic-gradient(#16a34a 0 ${sentPorcentaje}%, #e2e8f0 ${sentPorcentaje}% 100%)` }}
                  >
                    <span>{Number(sentPorcentaje).toFixed(0)}%</span>
                  </div>
                  <div className="sp-stats">
                    <span className="sp-chip">{sentTotal} destinatarios</span>
                    <span className="sp-chip ok">{sentLeidos} leídos</span>
                    <span className="sp-chip">{sentNoLeidos} no leídos</span>
                  </div>
                </div>

                <section className="sp-block">
                  <h4>Mensaje</h4>
                  <p className="sp-mail-body">{getMensajeTexto(sentDetailMessage) || 'Sin contenido.'}</p>
                </section>

                <div className="sp-search">
                  <i className="fas fa-search" aria-hidden="true" />
                  <input
                    type="text"
                    value={sentDetailSearch}
                    onChange={(event) => setSentDetailSearch(event.target.value)}
                    placeholder="Filtrar por nombre, mail o rol"
                    aria-label="Filtrar receptores"
                  />
                </div>

                <ul className="sp-recipients">
                  {filteredSentDetailRecipients.map((recipient) => (
                    <li key={recipient.id || recipient.Id || recipient.idUsuarioDestinatario || recipient.IdUsuarioDestinatario}>
                      <div>
                        <strong>{getDestinatarioNombre(recipient)}</strong>
                        <small>{[getDestinatarioEmail(recipient), getDestinatarioRol(recipient)].filter(Boolean).join(' · ')}</small>
                      </div>
                      {isDestinatarioLeido(recipient) ? (
                        <span className="sp-chip ok">Leído {formatDateTime(getDestinatarioFechaLectura(recipient))}</span>
                      ) : (
                        <span className="sp-chip">No leído</span>
                      )}
                    </li>
                  ))}
                  {filteredSentDetailRecipients.length === 0 && (
                    <li><small>No hay receptores para el filtro indicado.</small></li>
                  )}
                </ul>
              </>
            )}
          </StudentModal>
        )}

        <SubscriptionBlockedModal
          open={subscriptionBlockedOpen}
          message={subscriptionBlockedMessage || undefined}
          onClose={() => setSubscriptionBlockedOpen(false)}
        />
      </StudentShell>
    );
  }

  return (
    <div className={`new-dashboard admin-mode ${isParticularMode ? 'pd-pro' : ''} ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap" />
            <span>aluxIA</span>
          </div>
        </div>

        {renderMenu()}

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user" />
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{isParticularMode ? (isAlumno ? 'Alumno particular' : isAdmin ? 'Admin particular' : 'Profesor particular') : isAlumno ? 'Alumno' : isAdmin ? 'Admin' : 'Profesor'}</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main access-inbox-main">
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Mostrar menú"
            >
              <i className="fas fa-bars" />
            </button>
            <div>
              <h1 className="page-title">
                <i className="fas fa-envelope" />
                Mensajes
              </h1>
              <p className="page-subtitle">Bandeja de entrada de mensajes internos para {scopeName}.</p>
            </div>
          </div>
          <div className="header-right">
            {canCompose && (
              <button
                type="button"
                className="inbox-btn-header message-compose-open-btn"
                onClick={openComposer}
              >
                <i className="fas fa-paper-plane" />
                Enviar mensaje
              </button>
            )}
            <button
              type="button"
              className="inbox-btn-header"
              onClick={loadMensajes}
              title="Actualizar"
              disabled={loading}
            >
              <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-rotate'}`} />
              Actualizar
              {displayedNoLeidos > 0 && <span className="inbox-badge">{displayedNoLeidos > 99 ? '99+' : displayedNoLeidos}</span>}
            </button>
            {!isParticularMode && <ActiveColegioBadge />}
            <UserSessionMenu
              user={user}
              onLogout={logout}
              extraItems={isAdmin ? [{
                key: isParticularMode ? 'switch-particular' : 'switch-school',
                label: isParticularMode ? 'Cambiar particular' : 'Cambiar colegio',
                icon: 'fas fa-exchange-alt',
                onClick: handleBackToSelectColegio
              }] : []}
              showStandaloneLogout={false}
              userFallbackLabel={isParticularMode ? (isAlumno ? 'Alumno particular' : isAdmin ? 'Administrador' : 'Profesor particular') : isAlumno ? 'Alumno' : isAdmin ? 'Administrador' : 'Profesor'}
            />
          </div>
        </header>

        <div className="dashboard-content access-inbox-content">
          <div className="access-inbox-card access-inbox-page">
            <div className="access-inbox-header">
              <div>
                <h3>Mensajes internos</h3>
                <p>
                  {isSentMailbox
                    ? `${total} mensajes enviados`
                    : `${total} mensajes • ${displayedNoLeidos} no leídos`}
                </p>
              </div>
              <div className="message-mailbox-tabs">
                <button
                  type="button"
                  className={activeMailbox === 'recibidos' ? 'active' : ''}
                  onClick={() => handleMailboxChange('recibidos')}
                >
                  Recibidos
                </button>
                <button
                  type="button"
                  className={activeMailbox === 'enviados' ? 'active' : ''}
                  onClick={() => handleMailboxChange('enviados')}
                >
                  Enviados
                </button>
              </div>
            </div>
            {composerSuccess && (
              <div className="message-compose-success">
                <i className="fas fa-check-circle" />
                {composerSuccess}
              </div>
            )}

            <div className="access-inbox-toolbar">
              <div className="access-search">
                <i className="fas fa-search" />
                <input
                  type="text"
                  placeholder={isSentMailbox ? 'Buscar por asunto o contenido...' : 'Buscar por asunto, remitente o contenido...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              {!isSentMailbox && (
                <div className="access-filters">
                  <select value={filterLeido} onChange={(e) => setFilterLeido(e.target.value)}>
                    <option value="todos">Todos</option>
                    <option value="false">No leídos</option>
                    <option value="true">Leídos</option>
                  </select>
                </div>
              )}
            </div>

            <div className="access-inbox-body">
              <div className="access-inbox-list">
                {loading && <div className="access-inbox-loading">Cargando...</div>}
                {error && <div className="access-inbox-error">{error}</div>}
                {!loading && !error && mensajes.length === 0 && (
                  <div className="access-inbox-empty">
                    {isSentMailbox ? 'No hay mensajes enviados.' : 'No hay mensajes.'}
                  </div>
                )}
                {mensajes.map((item) => {
                  const id = getMensajeId(item);
                  const unread = isMensajeUnread(item);
                  const asunto = getMensajeAsunto(item);
                  const emisor = getMensajeEmisor(item);
                  const fecha = getMensajeFecha(item);
                  const estado = getMensajeEstado(item) || 'Enviado';
                  const destinatarios = getMensajeCantidadDestinatarios(item);
                  const leidos = getMensajeTotalLeidos(item);
                  const noLeidos = getMensajeTotalNoLeidos(item);
                  const pushErrores = getMensajeTotalPushErrores(item);
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`access-inbox-item ${!isSentMailbox && unread ? 'is-unread' : 'is-read'} ${getMensajeId(selected) === id ? 'is-active' : ''}`}
                      onClick={() => (isSentMailbox ? openSentDetail(item) : openReceivedDetail(item))}
                    >
                      <div className="access-item-header">
                        <span className="access-item-name">{asunto}</span>
                        <div className="access-item-tags">
                          {isSentMailbox ? (
                            <span className="access-item-badge read">{estado}</span>
                          ) : (
                            <span className={`access-item-badge ${unread ? 'unread' : 'read'}`}>
                              {unread ? 'No leído' : 'Leído'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="access-item-meta">
                        <span>{isSentMailbox ? `${destinatarios} destinatarios` : emisor}</span>
                        <span>{formatDateTime(fecha)}</span>
                      </div>
                      <div className="access-item-preview">
                        {isSentMailbox
                          ? `${leidos} leídos · ${noLeidos} no leídos${pushErrores > 0 ? ` · ${pushErrores} push con error` : ''}`
                          : buildPreview(getMensajeTexto(item))}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="access-inbox-detail">
                {loading && <div className="access-inbox-loading">Cargando detalle...</div>}
                {!loading && !selected && (
                  <div className="access-inbox-empty">
                    {isSentMailbox ? 'Selecciona un mensaje enviado para ver el detalle.' : 'Selecciona un mensaje para ver el detalle.'}
                  </div>
                )}
                {!loading && selected && (
                  <div
                    className={`access-detail-card ${!isSentMailbox && isMensajeUnread(selected) ? 'mark-read-on-click' : ''}`}
                    onClick={handleMarkSelectedAsRead}
                    aria-busy={updatingRead}
                  >
                    <div className="access-detail-header">
                      <div>
                        <h4>{getMensajeAsunto(selected)}</h4>
                        <div className="access-detail-title-meta">
                          <p>{isSentMailbox ? 'Mensaje enviado' : getMensajeEmisor(selected)}</p>
                          {!isSentMailbox && getMensajeEmail(selected) && <span>{getMensajeEmail(selected)}</span>}
                        </div>
                      </div>
                      <span className={`access-detail-status ${!isSentMailbox && isMensajeUnread(selected) ? 'unread' : 'read'}`}>
                        {isSentMailbox ? (getMensajeEstado(selected) || 'Enviado') : (isMensajeUnread(selected) ? 'No leído' : 'Leído')}
                      </span>
                    </div>

                    <div className="access-detail-meta">
                      <span><strong>Fecha:</strong> {formatDateTime(getMensajeFecha(selected))}</span>
                      {isSentMailbox ? (
                        <>
                          <span><strong>Destinatarios:</strong> {getMensajeCantidadDestinatarios(selected)}</span>
                          <span><strong>Encolados:</strong> {getMensajeCantidadEncolados(selected)}</span>
                          <span><strong>Leídos:</strong> {getMensajeTotalLeidos(selected)}</span>
                          <span><strong>No leídos:</strong> {getMensajeTotalNoLeidos(selected)}</span>
                          {getMensajeTotalPushErrores(selected) > 0 && (
                            <span className="access-detail-push-error"><strong>Push con error:</strong> {getMensajeTotalPushErrores(selected)}</span>
                          )}
                        </>
                      ) : (selected?.fechaLectura || selected?.FechaLectura ? (
                        <span><strong>Leído:</strong> {formatDateTime(selected.fechaLectura || selected.FechaLectura)}</span>
                      ) : null)}
                    </div>

                    <div className="access-detail-content">
                      {getMensajeTexto(selected) || 'Sin contenido.'}
                    </div>
                    {isSentMailbox && (
                      <div className="sent-detail-inline-actions">
                        <button type="button" className="btn-primary" onClick={(event) => {
                          event.stopPropagation();
                          openSentDetail(selected);
                        }}>
                          <i className="fas fa-chart-pie" />
                          Ver detalle de receptores
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {composerOpen && (
        <div className="modal-overlay message-compose-overlay">
          <form className="modal-card message-compose-modal-card" onSubmit={handleSendComposer}>
            <div className="message-compose-modal-header">
              <div>
                <h3>{canSendFamiliares && !composerAudience ? 'Redactar mensaje' : isFamiliaresComposer ? 'Mensaje a familiares' : 'Enviar mensaje'}</h3>
                <p>
                  {isParticularMode
                    ? (isParticularSegmentado
                      ? (isScopedProfesorComposer
                        ? 'Seleccioná profesores de la cuenta, alumnos de tus cursos o cursos completos.'
                        : 'Seleccioná destinatarios individuales o grupos completos de la cuenta.')
                      : 'Seleccioná uno o varios destinatarios habilitados para tu cuenta particular.')
                    : canSendFamiliares && !composerAudience
                    ? 'Elegí el tipo de destinatarios antes de redactar el contenido.'
                    : isFamiliaresComposer
                      ? (isProfesorComposer
                        ? 'Seleccioná familiares de tus cursos, de un alumno puntual o familiares individuales.'
                        : 'Seleccioná todos los familiares del ciclo actual, un curso, un alumno o familiares individuales.')
                      : isAlumnoComposer
                    ? 'Seleccioná compañeros de tu curso o profesores asociados a tus materias del año actual.'
                    : isProfesorComposer
                      ? 'Seleccioná alumnos de tus cursos o profesores de la institución.'
                      : 'Seleccioná destinatarios individuales o grupos completos del colegio.'}
                </p>
              </div>
              <button type="button" className="message-compose-close" onClick={closeComposer} disabled={composerSending}>
                <i className="fas fa-times" />
              </button>
            </div>

            {canSendFamiliares && !composerAudience && (
              <div className="message-compose-audience-grid">
                <button type="button" className="message-compose-audience-card" onClick={() => selectComposerAudience('institucional')}>
                  <i className="fas fa-school" />
                  <strong>Institucional</strong>
                  <span>Profesores, alumnos, cursos o grupos del colegio.</span>
                </button>
                <button type="button" className="message-compose-audience-card" onClick={() => selectComposerAudience('familiares')}>
                  <i className="fas fa-users" />
                  <strong>Familiares</strong>
                  <span>Familiares del ciclo lectivo actual según cursos y alumnos.</span>
                </button>
              </div>
            )}

            {composerLoading && (
              <div className="access-inbox-loading">Cargando destinatarios...</div>
            )}

            {composerError && (
              <div className="access-inbox-error message-compose-error">{composerError}</div>
            )}

            {(!canSendFamiliares || composerAudience) && (
              <>
            <div className="message-compose-form-grid">
              <label className="message-compose-field">
                <span>Asunto</span>
                <input
                  type="text"
                  value={composerForm.asunto}
                  onChange={(event) => updateComposerField('asunto', event.target.value)}
                  maxLength={200}
                  placeholder="Asunto del mensaje"
                  disabled={composerSending}
                />
              </label>

              <label className="message-compose-field">
                <span>Mensaje</span>
                <textarea
                  value={composerForm.mensaje}
                  onChange={(event) => updateComposerField('mensaje', event.target.value)}
                  rows={6}
                  placeholder="Escribí el mensaje para los destinatarios"
                  disabled={composerSending}
                />
              </label>

              {isParticularMode && !composerOptionsLoaded ? null : isParticularMode && !isParticularSegmentado ? (
                <div className="message-compose-section">
                  <div className="message-compose-section-title">
                    <strong>Destinatarios</strong>
                    <span>{composerOptions.usuarios.length} usuarios disponibles</span>
                  </div>
                  <SearchableSelect
                    multiple
                    values={composerForm.destinatarioIds}
                    onChange={(values) => updateComposerField('destinatarioIds', values)}
                    options={destinatarioOptions}
                    placeholder="Buscar por nombre, mail, rol o curso"
                    emptyText="No hay destinatarios disponibles."
                    noResultsText="No encontramos destinatarios."
                    disabled={composerSending || composerLoading}
                  />
                </div>
              ) : isFamiliaresComposer ? (
                <>
                  <div className="message-compose-section">
                    <div className="message-compose-section-title">
                      <strong>Destinatarios rápidos</strong>
                      <span>{composerOptions.totalFamiliares} familiares disponibles</span>
                    </div>
                    <div className="message-compose-check-grid one">
                      <label className="message-compose-check">
                        <input
                          type="checkbox"
                          checked={composerForm.incluirTodosLosFamiliares}
                          onChange={(event) => updateComposerField('incluirTodosLosFamiliares', event.target.checked)}
                          disabled={composerSending}
                        />
                        <span>{isProfesorComposer ? 'Todos los familiares de mis cursos' : 'Todos los familiares del ciclo actual'}</span>
                      </label>
                    </div>
                  </div>

                  <div className="message-compose-section">
                    <div className="message-compose-section-title">
                      <strong>Familiares por curso</strong>
                      <span>{isProfesorComposer ? 'Solo cursos asociados al profesor' : 'Cursos del ciclo lectivo actual'}</span>
                    </div>
                    <SearchableSelect
                      multiple
                      values={composerForm.cursoIds}
                      onChange={(values) => updateComposerField('cursoIds', values)}
                      options={cursoOptions}
                      placeholder="Buscar curso, año o división"
                      emptyText="No hay cursos con familiares disponibles."
                      noResultsText="No encontramos cursos."
                      disabled={composerSending || composerLoading || composerForm.incluirTodosLosFamiliares}
                    />
                  </div>

                  <div className="message-compose-section">
                    <div className="message-compose-section-title">
                      <strong>Familiares por alumno</strong>
                      <span>Seleccioná alumnos puntuales y se incluirán sus familiares</span>
                    </div>
                    <SearchableSelect
                      multiple
                      values={composerForm.alumnoFamiliarIds}
                      onChange={(values) => updateComposerField('alumnoFamiliarIds', values)}
                      options={alumnoFamiliaresOptions}
                      placeholder="Buscar alumno por nombre, mail o curso"
                      emptyText="No hay alumnos con familiares disponibles."
                      noResultsText="No encontramos alumnos."
                      disabled={composerSending || composerLoading || composerForm.incluirTodosLosFamiliares}
                    />
                  </div>

                  <div className="message-compose-section">
                    <div className="message-compose-section-title">
                      <strong>Familiares individuales</strong>
                      <span>Seleccioná uno o varios familiares particulares</span>
                    </div>
                    <SearchableSelect
                      multiple
                      values={composerForm.familiarIds}
                      onChange={(values) => updateComposerField('familiarIds', values)}
                      options={familiarOptions}
                      placeholder="Buscar familiar por nombre, mail, alumno o curso"
                      emptyText="No hay familiares disponibles."
                      noResultsText="No encontramos familiares."
                      disabled={composerSending || composerLoading || composerForm.incluirTodosLosFamiliares}
                    />
                  </div>
                </>
              ) : (
                <>
                  {!isAlumnoComposer && (
                    <div className="message-compose-section">
                      <div className="message-compose-section-title">
                        <strong>Destinatarios rápidos</strong>
                        <span>
                          {isScopedProfesorComposer
                            ? `${composerOptions.totalProfesores} profesores · ${composerOptions.totalAlumnos} alumnos de tus cursos`
                            : `${composerOptions.totalProfesores} profesores · ${composerOptions.totalAlumnos} alumnos`}
                        </span>
                      </div>
                      <div className="message-compose-check-grid">
                        {puedeEnviarATodos && (
                          <label className="message-compose-check">
                            <input
                              type="checkbox"
                              checked={composerForm.incluirTodaLaEscuela}
                              onChange={(event) => updateComposerField('incluirTodaLaEscuela', event.target.checked)}
                              disabled={composerSending}
                            />
                            <span>{isParticularMode ? 'Toda la cuenta' : 'Toda la escuela'}</span>
                          </label>
                        )}
                        <label className="message-compose-check">
                          <input
                            type="checkbox"
                            checked={composerForm.incluirTodosLosProfesores}
                            onChange={(event) => updateComposerField('incluirTodosLosProfesores', event.target.checked)}
                            disabled={composerSending || composerForm.incluirTodaLaEscuela}
                          />
                          <span>Todos los profesores</span>
                        </label>
                        <label className="message-compose-check">
                          <input
                            type="checkbox"
                            checked={composerForm.incluirTodosLosAlumnos}
                            onChange={(event) => updateComposerField('incluirTodosLosAlumnos', event.target.checked)}
                            disabled={composerSending || composerForm.incluirTodaLaEscuela}
                          />
                          <span>{isScopedProfesorComposer ? 'Todos mis alumnos' : 'Todos los alumnos'}</span>
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="message-compose-section">
                    <div className="message-compose-section-title">
                      <strong>{isAlumnoComposer ? 'Mis profesores' : 'Profesores individuales'}</strong>
                      <span>
                        {isAlumnoComposer
                          ? 'Solo docentes asociados a materias de tu curso actual'
                          : isParticularMode
                            ? 'Profesores de la cuenta, incluido el dueño'
                            : 'Seleccioná uno o varios profesores'}
                      </span>
                    </div>
                    <SearchableSelect
                      multiple
                      values={selectedProfesorIds}
                      onChange={(values) => updateDestinatariosForSet(profesorIdsSet, values)}
                      options={profesorOptions}
                      placeholder="Buscar profesor por nombre o mail"
                      emptyText={isAlumnoComposer ? 'No hay profesores asociados a tu curso actual.' : 'No hay profesores disponibles.'}
                      noResultsText="No encontramos profesores."
                      disabled={composerSending || composerLoading || composerForm.incluirTodosLosProfesores || composerForm.incluirTodaLaEscuela}
                    />
                  </div>

                  <div className="message-compose-section">
                    <div className="message-compose-section-title">
                      <strong>{isAlumnoComposer ? 'Compañeros de mi curso' : 'Alumnos individuales'}</strong>
                      <span>
                        {isAlumnoComposer
                          ? 'Solo compañeros de tu año y división actual'
                          : isScopedProfesorComposer
                            ? 'Solo alumnos de tus cursos asociados'
                            : 'Seleccioná uno o varios alumnos'}
                      </span>
                    </div>
                    <SearchableSelect
                      multiple
                      values={selectedAlumnoIds}
                      onChange={(values) => updateDestinatariosForSet(alumnoIdsSet, values)}
                      options={alumnoOptions}
                      placeholder={isAlumnoComposer ? 'Buscar compañero por nombre o mail' : 'Buscar alumno por nombre o mail'}
                      emptyText={isAlumnoComposer ? 'No hay compañeros disponibles para tu curso actual.' : 'No hay alumnos disponibles.'}
                      noResultsText={isAlumnoComposer ? 'No encontramos compañeros.' : 'No encontramos alumnos.'}
                      disabled={composerSending || composerLoading || composerForm.incluirTodosLosAlumnos || composerForm.incluirTodaLaEscuela}
                    />
                  </div>

                  {isParticularMode && preceptorOptions.length > 0 && (
                    <div className="message-compose-section">
                      <div className="message-compose-section-title">
                        <strong>Preceptores</strong>
                        <span>Seleccioná uno o varios preceptores de la cuenta</span>
                      </div>
                      <SearchableSelect
                        multiple
                        values={selectedPreceptorIds}
                        onChange={(values) => updateDestinatariosForSet(preceptorIdsSet, values)}
                        options={preceptorOptions}
                        placeholder="Buscar preceptor por nombre o mail"
                        emptyText="No hay preceptores disponibles."
                        noResultsText="No encontramos preceptores."
                        disabled={composerSending || composerLoading || composerForm.incluirTodaLaEscuela}
                      />
                    </div>
                  )}

                  {!isAlumnoComposer && (
                    <div className="message-compose-section">
                      <div className="message-compose-section-title">
                        <strong>Alumnos por curso</strong>
                        <span>{isScopedProfesorComposer ? 'Seleccioná uno o varios de tus cursos completos' : 'Seleccioná cursos completos'}</span>
                      </div>
                      <SearchableSelect
                        multiple
                        values={composerForm.cursoIds}
                        onChange={(values) => updateComposerField('cursoIds', values)}
                        options={cursoOptions}
                        placeholder={isParticularMode ? 'Buscar curso por nombre' : 'Buscar curso, año o división'}
                        emptyText="No hay cursos disponibles."
                        noResultsText="No encontramos cursos."
                        disabled={composerSending || composerLoading || composerForm.incluirTodosLosAlumnos || composerForm.incluirTodaLaEscuela}
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="message-compose-summary">
              <i className="fas fa-users" />
              <span>{selectedRecipientCount} {selectedRecipientCount === 1 ? 'destinatario seleccionado' : 'destinatarios seleccionados'}</span>
            </div>

            <div className="modal-actions message-compose-actions">
              <button type="button" className="btn-secondary" onClick={closeComposer} disabled={composerSending}>
                Cancelar
              </button>
              <button type="submit" className="btn-primary success" disabled={composerSending || composerLoading}>
                {composerSending && <i className="fas fa-spinner fa-spin" />}
                Enviar mensaje
              </button>
            </div>
              </>
            )}
          </form>
        </div>
      )}

      {sentDetailOpen && (() => {
        const detailMessage = getDetalleMensaje(sentDetail) || selected;
        const totalLeidos = getDetalleTotalLeidos(sentDetail);
        const totalNoLeidos = getDetalleTotalNoLeidos(sentDetail);
        const totalDestinatarios = totalLeidos + totalNoLeidos;
        const porcentajeLeidos = getDetallePorcentajeLeidos(sentDetail) || (totalDestinatarios > 0 ? Math.round((totalLeidos * 10000) / totalDestinatarios) / 100 : 0);
        const chartStyle = {
          background: `conic-gradient(#16a34a 0 ${porcentajeLeidos}%, #e11d48 ${porcentajeLeidos}% 100%)`
        };

        return (
          <div className="modal-overlay message-sent-detail-overlay">
            <article className="modal-card message-sent-detail-modal">
              <div className="message-compose-modal-header">
                <div>
                  <h3>Detalle de mensaje enviado</h3>
                  <p>{getMensajeAsunto(detailMessage)}</p>
                </div>
                <button type="button" className="message-compose-close" onClick={closeSentDetail}>
                  <i className="fas fa-times" />
                </button>
              </div>

              {sentDetailLoading && <div className="access-inbox-loading">Cargando detalle...</div>}
              {sentDetailError && <div className="access-inbox-error">{sentDetailError}</div>}

              {!sentDetailLoading && !sentDetailError && detailMessage && (
                <>
                  <div className="sent-detail-summary">
                    <div className="sent-detail-message-card">
                      <span>Mensaje</span>
                      <h4>{getMensajeAsunto(detailMessage)}</h4>
                      <p>{getMensajeTexto(detailMessage) || 'Sin contenido.'}</p>
                      <div className="sent-detail-message-meta">
                        <span><strong>Fecha:</strong> {formatDateTime(getMensajeFecha(detailMessage))}</span>
                        <span><strong>Destinatarios:</strong> {getMensajeCantidadDestinatarios(detailMessage)}</span>
                        <span><strong>Encolados:</strong> {getMensajeCantidadEncolados(detailMessage)}</span>
                      </div>
                    </div>

                    <div className="sent-detail-chart-card">
                      <div className="sent-detail-donut" style={chartStyle}>
                        <span>{porcentajeLeidos.toFixed(0)}%</span>
                      </div>
                      <div className="sent-detail-chart-legend">
                        <span><i className="legend-dot read" /> Leídos: {totalLeidos}</span>
                        <span><i className="legend-dot unread" /> No leídos: {totalNoLeidos}</span>
                      </div>
                    </div>
                  </div>

                  <div className="sent-detail-toolbar">
                    <div className="access-search">
                      <i className="fas fa-search" />
                      <input
                        type="text"
                        value={sentDetailSearch}
                        onChange={(event) => setSentDetailSearch(event.target.value)}
                        placeholder="Filtrar por nombre, mail, rol o estado de push..."
                      />
                    </div>
                    <span>{filteredSentDetailRecipients.length} de {getDetalleDestinatarios(sentDetail).length} receptores</span>
                  </div>

                  <div className="sent-detail-table-wrap">
                    <table className="sent-detail-table">
                      <thead>
                        <tr>
                          <th>Receptor</th>
                          <th>Rol</th>
                          <th>Lectura</th>
                          {!isParticularMode && <th>Push</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSentDetailRecipients.map((recipient) => {
                          const pushMeta = getPushStatusMeta(getDestinatarioPushEstado(recipient));
                          const pushErrores = getDestinatarioPushErrores(recipient);
                          return (
                            <tr key={recipient.id || recipient.Id || recipient.idUsuarioDestinatario || recipient.IdUsuarioDestinatario}>
                              <td>
                                <strong>{getDestinatarioNombre(recipient)}</strong>
                                <span>{getDestinatarioEmail(recipient)}</span>
                              </td>
                              <td>{getDestinatarioRol(recipient)}</td>
                              <td>
                                {isDestinatarioLeido(recipient) ? (
                                  <span className="recipient-read-status read">Leído {formatDateTime(getDestinatarioFechaLectura(recipient))}</span>
                                ) : (
                                  <span className="recipient-read-status unread">No leído</span>
                                )}
                              </td>
                              {!isParticularMode && (
                                <td>
                                  <span className={`recipient-push-status ${pushMeta.tone}`}>{pushMeta.label}</span>
                                  <small>
                                    {getDestinatarioPushEnviadas(recipient)}/{getDestinatarioPushDispositivos(recipient)} dispositivos
                                    {getDestinatarioFechaPush(recipient) ? ` · ${formatDateTime(getDestinatarioFechaPush(recipient))}` : ''}
                                  </small>
                                  {(pushErrores > 0 || getDestinatarioPushError(recipient)) && (
                                    <em>{getDestinatarioPushError(recipient) || `${pushErrores} error/es de push`}</em>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                        {filteredSentDetailRecipients.length === 0 && (
                          <tr>
                            <td colSpan={isParticularMode ? 3 : 4} className="sent-detail-empty">No hay receptores para el filtro indicado.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </article>
          </div>
        );
      })()}
      <SubscriptionBlockedModal
        open={subscriptionBlockedOpen}
        message={subscriptionBlockedMessage || undefined}
        onClose={() => setSubscriptionBlockedOpen(false)}
      />
    </div>
  );
};

export default MensajesInternosInbox;
